import { useTranslation } from 'react-i18next';
import { ExternalLink } from 'lucide-react';
import type { ProcurementBlockData } from '@foco/contracts';
import { formatCurrency, formatDate } from '../../../shared/lib/format';
import { useLocale } from './primitives';

/**
 * Public procurement.
 *
 * Also renders the honest note about what this source can answer. PNCP exposes
 * no supplier-side filter (see the adapter's documentation), so the card must
 * not let a user read "0 contracts" as "this company has never won a public
 * contract" when the question could not be asked at all.
 */
export function ProcurementBlock({ data }: { data: unknown }): React.ReactNode {
  const { t } = useTranslation();
  const locale = useLocale();
  const block = (data ?? {}) as ProcurementBlockData;
  const rows = block.recent ?? [];

  return (
    <div>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium">
          {t('blocks.procurement.total', { count: block.total ?? rows.length })}
        </p>
        {block.totalAmount !== null && block.totalAmount !== undefined && (
          <p className="text-sm text-[var(--color-muted-fg)]">
            {t('blocks.procurement.amount')}:{' '}
            {formatCurrency(block.totalAmount, block.currency ?? 'BRL', locale)}
          </p>
        )}
      </div>

      {rows.length > 0 && (
        <ul className="mt-3 divide-y divide-[var(--color-border)]">
          {rows.map((row) => (
            <li key={row.externalId} className="py-2 first:pt-0 last:pb-0">
              <p className="line-clamp-2 text-sm">{row.title ?? '—'}</p>
              <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-[var(--color-muted-fg)]">
                {row.buyerName && <span>{row.buyerName}</span>}
                {row.publishedAt && <span>{formatDate(row.publishedAt, locale)}</span>}
                {row.sourceUrl && (
                  <a
                    href={row.sourceUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-0.5 hover:text-brand-600 hover:underline"
                  >
                    {t('lookup.openSource')}
                    <ExternalLink aria-hidden className="h-3 w-3" />
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

